import type { ReactElement } from 'react';

import { remainingTimeLabel } from './formatters';
import type { ImportTask, ImportTaskProgressJson } from './types';

type ProgressSectionProps = {
  progress: ImportTaskProgressJson;
  startedAt: ImportTask['startedAt'];
};

function phaseLabel(phase: ImportTaskProgressJson['phase']): string {
  switch (phase) {
    case 'loading':
      return 'CSVを読み込み中…';
    case 'staging':
      return 'データを準備中…';
    case 'diffing':
      return '差分を計算中…';
    case 'planning':
      return '取込計画を作成中…';
    case 'sweeping':
      return '不要データを削除中…';
    default:
      // 'processing' = apply 本体
      return '行を取り込み中…';
  }
}

function progressPercent(progress: ImportTaskProgressJson): number | undefined {
  const { processedRows, totalRows } = progress;
  if (totalRows === undefined || totalRows <= 0) return undefined;
  return Math.min(100, Math.round((processedRows / totalRows) * 100));
}

type ProgressMetricProps = {
  label: string;
  value: number;
  tone?: 'default' | 'positive' | 'negative';
};

function ProgressMetric({ label, value, tone = 'default' }: ProgressMetricProps): ReactElement {
  const valueClass = tone === 'positive' ? 'text-positive' : tone === 'negative' ? 'text-negative' : 'text-default';

  return (
    <div className="rounded-lg border border-default bg-surface p-4">
      <dt className="text-xs font-medium text-muted-light">{label}</dt>
      <dd className={`mt-2 text-2xl font-semibold tabular-nums ${valueClass}`}>{value}</dd>
    </div>
  );
}

export function ProgressSection({ progress, startedAt }: ProgressSectionProps): ReactElement {
  const percent = progressPercent(progress);
  const totalRows = progress.totalRows;
  const remainingLabel = remainingTimeLabel(progress, startedAt);
  // totalRows 未確定 (apply 前の load/staging/diff/plan) は行レベルの % を出せないため、
  // 0% 固定の静的バーではなく不確定アニメーションバーを表示して「処理中」であることを示す。
  const indeterminate = percent === undefined;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <h2 className="m-0 text-base font-semibold text-default">取り込み進捗</h2>
        <p className="m-0 text-xs text-muted-light">{phaseLabel(progress.phase)}</p>
      </div>

      <div className="flex flex-col gap-4 rounded-xl border border-default p-4">
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
            <span className="text-muted-light">処理済み行数</span>
            <span className="font-medium tabular-nums text-default">
              {totalRows !== undefined ? (
                <>
                  {progress.processedRows.toLocaleString()} / {totalRows.toLocaleString()} 行
                  {percent !== undefined ? ` (${percent}%)` : null}
                </>
              ) : (
                // 前段フェーズは件数を出せないので「準備中…」を表示する (0 行表示で止まって見えるのを避ける)。
                '準備中…'
              )}
            </span>
          </div>
          <p className="m-0 text-xs text-muted-light">{remainingLabel}</p>

          <div
            className="relative h-2 overflow-hidden rounded-full bg-subtle"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={totalRows ?? 100}
            aria-valuenow={indeterminate ? undefined : progress.processedRows}
            aria-label="取り込み進捗"
          >
            {indeterminate ? (
              <div className="import-progress-indeterminate rounded-full bg-primary" />
            ) : (
              <div className="h-full bg-primary transition-all duration-300" style={{ width: `${percent}%` }} />
            )}
          </div>
        </div>

        <dl className="grid gap-3 sm:grid-cols-3">
          <ProgressMetric label="新規" value={progress.created} tone="positive" />
          <ProgressMetric label="更新" value={progress.updated} />
          <ProgressMetric label="失敗" value={progress.failed} tone="negative" />
        </dl>
      </div>
    </section>
  );
}
